import OrganizationLogo from 'src/components/Organization/OrganizationLogo';
import { IconLogo } from 'src/components/Svg/Images';
import { cn } from 'src/lib/utils';
import { getAvatarColor, getAvatarColorClassName, getInitials } from 'src/widgets/AppCard/model/identity';

import { getAvatarShape, type Ownership } from '../model';

const SHAPE_CLASS_NAME = {
  round: 'rounded-full',
  square: 'rounded-md',
};

type AvatarSize = 'default' | 'sm';

const SIZE_CLASS_NAME: Record<AvatarSize, string> = {
  default: 'size-13',
  sm: 'size-9',
};

const FALLBACK_TEXT_CLASS_NAME: Record<AvatarSize, string> = {
  default: '',
  sm: 'text-xs',
};

type AvatarProps = {
  appId: string;
  name: string | null;
  logoUrl: string | null;
  claimed: boolean;
  ownership: Ownership;
  size?: AvatarSize;
  className?: string;
};

const Avatar = ({ appId, name, logoUrl, claimed, ownership, size = 'default', className }: AvatarProps) => {
  const shape = getAvatarShape(claimed, ownership, logoUrl !== null);
  const shapeClassName = SHAPE_CLASS_NAME[shape];
  const sizeClassName = SIZE_CLASS_NAME[size];

  if (!claimed) {
    return (
      <div
        role="img"
        aria-label="App avatar"
        data-shape={shape}
        data-variant="unclaimed"
        className={cn('flex shrink-0 items-center justify-center bg-border', sizeClassName, shapeClassName, className)}
      >
        <IconLogo className="size-7 opacity-20 grayscale" />
      </div>
    );
  }

  const color = getAvatarColor(appId);

  return (
    <div role="img" aria-label="App avatar" data-shape={shape} className={cn(className)}>
      <OrganizationLogo
        name={name ?? ''}
        logo={logoUrl ?? undefined}
        shouldFetchLogo={false}
        fallbackText={getInitials(name ?? '')}
        classes={{
          fallback: cn(
            FALLBACK_TEXT_CLASS_NAME[size],
            logoUrl ? 'bg-white text-foreground' : cn('text-white', getAvatarColorClassName(color), shapeClassName),
          ),
        }}
        className={cn('shrink-0 border-0 font-semibold', sizeClassName, shapeClassName, logoUrl && 'shadow-sm')}
      />
    </div>
  );
};

export { Avatar };
export type { AvatarProps, AvatarSize };
